import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown } from "lucide-react";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
  }, [location]);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/services", label: "Services", hasDropdown: true },
    { path: "/doing-business-bosnia", label: "Doing Business in BiH" },
    { path: "/industries", label: "Industries" },
    { path: "/blog", label: "Insights" },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
  ];

  const serviceCategories = [
    { path: "/services/corporate", label: "Corporate Services" },
    { path: "/services/accounting", label: "Accounting Services" },
    { path: "/services/tax", label: "Tax Services" },
    { path: "/services/trusts", label: "Trusts, Escrow & Fiduciary" },
    { path: "/services/citizenship", label: "Citizenship & Residence" },
    { path: "/services/relocation", label: "Business Relocation" },
    { path: "/services/compliance", label: "Risk & Compliance" },
    { path: "/services/financial", label: "Regulated Financial Services" },
    { path: "/services/ip", label: "Intellectual Property" },
    { path: "/services/employment", label: "Employment Advisory" },
    { path: "/services/ma", label: "Mergers & Acquisitions" },
  ];

  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: "https://calendly.com/franjo-vucic-d0pg/30min",
      });
    }
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100"
          : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <span className="text-xl md:text-2xl font-semibold text-[#0B2A4A] font-['Sora']">
              VTZ<span className="text-[#2F6FED]">Backoffice</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center space-x-6" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <div key={link.path} className="relative group">
                {link.hasDropdown ? (
                  <div
                    className="flex items-center gap-1 cursor-pointer"
                    onMouseEnter={() => setIsServicesOpen(true)}
                    onMouseLeave={() => setIsServicesOpen(false)}
                  >
                    <Link
                      to={link.path}
                      data-testid={`nav-link-${link.label.toLowerCase()}`}
                      className={`text-sm font-medium transition-colors duration-200 ${
                        location.pathname.startsWith("/services")
                          ? "text-[#2F6FED]"
                          : "text-slate-600 hover:text-[#0B2A4A]"
                      }`}
                    >
                      {link.label}
                    </Link>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                    
                    {/* Dropdown */}
                    {isServicesOpen && (
                      <div className="absolute top-full left-0 pt-2 w-64">
                        <div className="bg-white rounded-lg shadow-lg border border-slate-100 py-2 max-h-96 overflow-y-auto">
                          {serviceCategories.map((cat) => (
                            <Link
                              key={cat.path}
                              to={cat.path}
                              className="block px-4 py-2 text-sm text-slate-600 hover:bg-[#F5F7FA] hover:text-[#0B2A4A] transition-colors"
                            >
                              {cat.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={link.path}
                    data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                      location.pathname === link.path
                        ? "text-[#2F6FED]"
                        : "text-slate-600 hover:text-[#0B2A4A]"
                    }`}
                  >
                    {link.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden xl:block">
            <Button
              data-testid="cta-book-call-header"
              onClick={openCalendly}
              className="bg-[#2F6FED] hover:bg-[#2558be] text-white font-medium px-5 py-2.5 rounded-md transition-all duration-200 shadow-sm hover:shadow-md"
            >
              Schedule a Call
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="xl:hidden p-2 text-slate-600 hover:text-[#0B2A4A]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="xl:hidden bg-white border-t border-slate-100 shadow-lg max-h-[80vh] overflow-y-auto"
        >
          <nav className="max-w-7xl mx-auto px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <div key={link.path}>
                {link.hasDropdown ? (
                  <>
                    <button
                      onClick={() => setIsServicesOpen(!isServicesOpen)}
                      className="flex items-center justify-between w-full py-3 px-4 rounded-lg text-sm font-medium text-slate-600 hover:bg-[#F5F7FA]"
                    >
                      <span>{link.label}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isServicesOpen && (
                      <div className="ml-4 mt-1 space-y-1">
                        {serviceCategories.map((cat) => (
                          <Link
                            key={cat.path}
                            to={cat.path}
                            className="block py-2 px-4 text-sm text-slate-500 hover:text-[#0B2A4A] hover:bg-[#F5F7FA] rounded-lg"
                          >
                            {cat.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={link.path}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`block py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname === link.path
                        ? "bg-[#F5F7FA] text-[#2F6FED]"
                        : "text-slate-600 hover:bg-[#F5F7FA] hover:text-[#0B2A4A]"
                    }`}
                  >
                    {link.label}
                  </Link>
                )}
              </div>
            ))}
            <div className="pt-2">
              <Button
                data-testid="cta-book-call-mobile"
                onClick={openCalendly}
                className="w-full bg-[#2F6FED] hover:bg-[#2558be] text-white font-medium py-3 rounded-md"
              >
                Schedule a Call
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
